/**
 * Audit-Welle A — Zentraler Error-Logger.
 *
 * Ersetzt die vielen `.catch(() => {})`-Schlucker in der App. Schreibt
 * strukturiert in v5_error_log + Console (für Browser-DevTools).
 *
 * Fire-and-forget — wenn das Schreiben SELBST fehlschlägt, fallen wir
 * stillschweigend zurück. Wir wollen nicht, dass der Error-Logger seine
 * eigene Welle von Errors erzeugt.
 */

import { supabase } from './supabase';

export type ErrorSeverity = 'info' | 'warn' | 'error' | 'critical';

export interface ErrorLogEntry {
  scope: string;
  symbol?: string | null;
  errorClass?: string | null;
  message: string;
  severity?: ErrorSeverity;
  context?: Record<string, unknown> | null;
}

/**
 * Klassifiziert eine geworfene Fehler-Instanz in eine Maschinen-Klasse.
 * Hilft beim Filtern im cron-health-Dashboard.
 */
export function classifyError(err: unknown): string {
  if (err instanceof Error) {
    const msg = err.message.toLowerCase();
    if (msg.includes('timeout') || msg.includes('aborted')) return 'timeout';
    if (msg.includes('rate limit') || msg.includes('429')) return 'http_429';
    if (msg.includes('rls') || msg.includes('row-level security')) return 'rls_denied';
    if (msg.includes('json') || msg.includes('parse')) return 'json_parse';
    if (msg.includes('network') || msg.includes('fetch')) return 'network';
    if (msg.includes('not found') || msg.includes('404')) return 'not_found';
    if (msg.includes('500') || msg.includes('502') || msg.includes('503')) return 'http_5xx';
    if (msg.includes('400') || msg.includes('401') || msg.includes('403')) return 'http_4xx';
    return err.name || 'error';
  }
  if (typeof err === 'string') return 'string';
  return 'unknown';
}

/**
 * Schreibt einen Error-Log-Eintrag in v5_error_log.
 * NICHT awaiten — Fire-and-forget. Falls die DB nicht erreichbar ist,
 * landet der Fehler nur in der Console, damit der Helper nicht selbst
 * zur Fehlerquelle wird.
 */
export function logError(entry: ErrorLogEntry): void {
  const severity = entry.severity ?? 'warn';

  // Console-Log (immer, auch für lokale Entwicklung)
  const consoleMethod =
    severity === 'critical' || severity === 'error' ? 'error' : 'warn';
  // eslint-disable-next-line no-console
  console[consoleMethod](
    `[${entry.scope}]${entry.symbol ? ` ${entry.symbol}` : ''} ${entry.errorClass ?? ''}: ${entry.message}`,
    entry.context ?? ''
  );

  // DB-Insert Fire-and-Forget
  void supabase
    .from('v5_error_log')
    .insert({
      scope: entry.scope,
      symbol: entry.symbol ?? null,
      error_class: entry.errorClass ?? null,
      message: entry.message.slice(0, 1000), // safety cap
      severity,
      context: entry.context ?? null,
    })
    .then(({ error }) => {
      if (error) {
        // Logger-Selbst-Fehler nur in Console, nicht rekursiv weiter logen
        // eslint-disable-next-line no-console
        console.error('[error-log] insert failed:', error.message);
      }
    });
}

/**
 * Convenience-Wrapper: nimmt eine geworfene Fehler-Instanz und klassifiziert
 * sie automatisch. Typisches Pattern in Promise-Catches.
 *
 * @example
 *   .catch((err) => logCaughtError('cached-provider.writeQuote', err, { symbol }))
 */
export function logCaughtError(
  scope: string,
  err: unknown,
  extras: { symbol?: string; severity?: ErrorSeverity; context?: Record<string, unknown> } = {}
): void {
  const message =
    err instanceof Error
      ? err.message
      : typeof err === 'string'
        ? err
        : 'Unknown error';
  logError({
    scope,
    symbol: extras.symbol,
    errorClass: classifyError(err),
    message,
    severity: extras.severity ?? 'warn',
    context: extras.context,
  });
}

/**
 * Read-API für das Admin-Dashboard.
 * Liefert die letzten N Error-Einträge sortiert nach Zeit.
 */
export interface ErrorLogRow {
  id: number;
  occurred_at: string;
  scope: string;
  symbol: string | null;
  error_class: string | null;
  message: string;
  severity: ErrorSeverity;
  context: Record<string, unknown> | null;
}

export async function fetchRecentErrors(
  options: { limit?: number; sinceHours?: number; minSeverity?: ErrorSeverity } = {},
  signal?: AbortSignal
): Promise<ErrorLogRow[]> {
  if (signal?.aborted) return [];
  const limit = options.limit ?? 100;
  const sinceHours = options.sinceHours ?? 24;
  const since = new Date(Date.now() - sinceHours * 60 * 60 * 1000).toISOString();

  let q = supabase
    .from('v5_error_log')
    .select('*')
    .gte('occurred_at', since)
    .order('occurred_at', { ascending: false })
    .limit(limit);

  if (options.minSeverity) {
    const order: ErrorSeverity[] = ['info', 'warn', 'error', 'critical'];
    const min = order.indexOf(options.minSeverity);
    const allowed = order.slice(min);
    q = q.in('severity', allowed);
  }

  const { data, error } = await q;
  if (error) {
    console.warn('[error-log] fetch failed:', error.message);
    return [];
  }
  return (data ?? []) as ErrorLogRow[];
}
